import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { useCollectionMonitoring, useCollectionRequests } from '../../hooks'
import type {
  MonitoringSummary,
  MonitoringFilterParams,
  SimplifiedStatus,
} from '../../types/collection-targets'
import { SIMPLIFIED_STATUS_LABELS, SIMPLIFIED_STATUS_COLOURS } from '../../types/collection-targets'
import { MonitoringTable } from './MonitoringTable'

type SortColumn = 'hostname' | 'status' | 'collectionNumber' | 'completedAt' | 'request'

type SortDirection = 'asc' | 'desc'

interface CollectionMonitoringViewProps {
  caseId: string
  onNavigateToRequest: (requestId: string) => void
}

interface SummaryCardsProps {
  summary: MonitoringSummary
}

function SummaryCards({ summary }: SummaryCardsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <SummaryCard
        label={t('collections.monitoring.totalTargets')}
        value={summary.totalTargets}
        colour={null}
      />
      <SummaryCard
        label={t('collections.monitoring.requested')}
        value={summary.requestedCount}
        colour={SIMPLIFIED_STATUS_COLOURS.requested}
      />
      <SummaryCard
        label={t('collections.monitoring.returned')}
        value={summary.returnedCount}
        colour={SIMPLIFIED_STATUS_COLOURS.returned}
      />
      <SummaryCard
        label={t('collections.monitoring.issues')}
        value={summary.issueCount}
        colour={SIMPLIFIED_STATUS_COLOURS.issue}
      />
      <SummaryCard
        label={t('collections.monitoring.recollections')}
        value={summary.recollectionCount}
        colour={null}
      />
      <SummaryCard
        label={t('collections.monitoring.uniqueEndpoints')}
        value={summary.uniqueEndpoints}
        colour={null}
      />
    </div>
  )
}

interface SummaryCardProps {
  label: string
  value: number
  colour: string | null
}

function SummaryCard({ label, value, colour }: SummaryCardProps): React.ReactElement {
  const borderStyle = colour ? { borderColor: colour } : undefined
  return (
    <div
      className="rounded-lg border border-slate-200 p-3 text-center dark:border-slate-700"
      style={borderStyle}
    >
      <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  )
}

interface MonitoringFiltersProps {
  filters: MonitoringFilterParams
  requestOptions: Array<{ id: string; requestName: string }>
  onFiltersChange: (filters: MonitoringFilterParams) => void
}

function MonitoringFilters({
  filters,
  requestOptions,
  onFiltersChange,
}: MonitoringFiltersProps): React.ReactElement {
  const { t } = useTranslation('cases')

  const handleStatusChange = (value: string): void => {
    const next: MonitoringFilterParams = { ...filters }
    if (value === '') {
      delete next.status
    } else {
      next.status = value as SimplifiedStatus
    }
    onFiltersChange(next)
  }

  const handleRequestChange = (value: string): void => {
    const next: MonitoringFilterParams = { ...filters }
    if (value === '') {
      delete next.requestId
    } else {
      next.requestId = value
    }
    onFiltersChange(next)
  }

  const handleHostnameChange = (value: string): void => {
    const next: MonitoringFilterParams = { ...filters }
    if (value === '') {
      delete next.hostname
    } else {
      next.hostname = value
    }
    onFiltersChange(next)
  }

  return (
    <div className="flex flex-wrap gap-3">
      <select
        value={filters.status ?? ''}
        onChange={(ev) => {
          handleStatusChange(ev.target.value)
        }}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('collections.monitoring.filterAll')}</option>
        {(Object.keys(SIMPLIFIED_STATUS_LABELS) as SimplifiedStatus[]).map((st) => (
          <option key={st} value={st}>
            {SIMPLIFIED_STATUS_LABELS[st]}
          </option>
        ))}
      </select>
      <select
        value={filters.requestId ?? ''}
        onChange={(ev) => {
          handleRequestChange(ev.target.value)
        }}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('collections.monitoring.filterAllRequests')}</option>
        {requestOptions.map((req) => (
          <option key={req.id} value={req.id}>
            {req.requestName}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={filters.hostname ?? ''}
        onChange={(ev) => {
          handleHostnameChange(ev.target.value)
        }}
        placeholder={t('collections.monitoring.searchHostname')}
        className="min-w-48 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      />
    </div>
  )
}

interface EmptyStateProps {
  onNavigateToRequests: () => void
}

function EmptyState({ onNavigateToRequests }: EmptyStateProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
      <p className="text-slate-600 dark:text-slate-400">{t('collections.monitoring.noData')}</p>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        {t('collections.monitoring.noDataHint')}
      </p>
      <button
        onClick={onNavigateToRequests}
        className="mt-4 text-sm text-[var(--accent)] hover:underline"
      >
        {t('collections.monitoring.tabRequests')}
      </button>
    </div>
  )
}

function LoadingPanel({ label }: { label: string }): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center" role="status" aria-busy="true">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[var(--accent)]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      </div>
    </div>
  )
}

function ErrorPanel({
  message,
  onRetry,
  retryLabel,
}: {
  message: string
  onRetry: () => void
  retryLabel: string
}): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center" role="alert">
        <p className="text-red-600 dark:text-red-400">{message}</p>
        <button onClick={onRetry} className="mt-4 text-sm text-[var(--accent)] hover:underline">
          {retryLabel}
        </button>
      </div>
    </div>
  )
}

function ViewHeader(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <h3 className="text-base font-[var(--font-display)] font-semibold text-slate-900 dark:text-slate-100">
        {t('collections.monitoring.title')}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('collections.monitoring.subtitle')}
      </p>
    </div>
  )
}

export function CollectionMonitoringView({
  caseId,
  onNavigateToRequest,
}: CollectionMonitoringViewProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const [filters, setFilters] = useState<MonitoringFilterParams>({})
  const [sortColumn, setSortColumn] = useState<SortColumn>('hostname')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const { items, summary, loading, error, refetch } = useCollectionMonitoring(caseId, filters)
  const { requests } = useCollectionRequests(caseId)

  const handleSort = useCallback((col: SortColumn): void => {
    setSortColumn((prev) => {
      if (prev === col) {
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'))
        return col
      }
      setSortDirection('asc')
      return col
    })
  }, [])

  const handleNavigateToRequests = useCallback((): void => {
    const first = requests[0]
    if (first) onNavigateToRequest(first.id)
  }, [requests, onNavigateToRequest])

  if (loading) return <LoadingPanel label={t('collections.monitoring.title')} />
  if (error)
    return (
      <ErrorPanel
        message={error}
        onRetry={() => {
          void refetch()
        }}
        retryLabel={t('actions.tryAgain')}
      />
    )

  return (
    <div className="space-y-4">
      <ViewHeader />
      {summary && <SummaryCards summary={summary} />}
      <MonitoringFilters filters={filters} requestOptions={requests} onFiltersChange={setFilters} />
      {items.length === 0 ? (
        <EmptyState onNavigateToRequests={handleNavigateToRequests} />
      ) : (
        <MonitoringTable
          items={items}
          caseId={caseId}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          onNavigateToRequest={onNavigateToRequest}
          onRefetch={() => {
            void refetch()
          }}
        />
      )}
    </div>
  )
}
